import { cloneElement, useEffect, useId, useState, type ReactElement } from 'react';

interface TooltipProps {
  text: string;
  children: ReactElement<{ 'aria-describedby'?: string }>;
  block?: boolean;
  anchorToParent?: boolean;
  className?: string;
}

export function Tooltip({ text, children, block = false, anchorToParent = false, className = '' }: TooltipProps) {
  const id = useId();
  const [isOpen, setIsOpen] = useState(false);
  const showTooltip = isOpen && Boolean(text);
  const Container = block ? 'div' : 'span';
  const describedBy = [children.props['aria-describedby'], showTooltip ? id : undefined]
    .filter(Boolean).join(' ') || undefined;

  useEffect(() => {
    if (!showTooltip) return;
    const dismiss = () => setIsOpen(false);
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') setIsOpen(false);
    };
    // Listen before jsCoq's document-level shortcuts consume Escape.
    window.addEventListener('keydown', handleKeyDown, true);
    window.addEventListener('pointerdown', dismiss, true);
    window.addEventListener('scroll', dismiss, true);
    return () => {
      window.removeEventListener('keydown', handleKeyDown, true);
      window.removeEventListener('pointerdown', dismiss, true);
      window.removeEventListener('scroll', dismiss, true);
    };
  }, [showTooltip]);

  return (
    <Container
      className={`${anchorToParent ? '' : 'relative'} ${block ? 'block' : 'inline-flex'} ${className}`}
      onPointerEnter={event => {
        // Touch can synthesize mouse events and leave hover/focus behind after navigation.
        if (event.pointerType === 'mouse') setIsOpen(true);
      }}
      onPointerLeave={() => setIsOpen(false)}
      onPointerDownCapture={() => setIsOpen(false)}
      onClickCapture={() => setIsOpen(false)}
      onFocus={event => {
        if (event.target.matches(':focus-visible')) setIsOpen(true);
      }}
      onBlur={() => setIsOpen(false)}
    >
      {cloneElement(children, { 'aria-describedby': describedBy })}
      {showTooltip && (
        <span id={id} role="tooltip" className={`absolute z-50 ${block ? 'inset-x-0 bottom-full pb-2' : 'left-1/2 top-full -translate-x-1/2 pt-2'}`}>
          <span className={`block rounded bg-gray-900 px-2 py-1 text-xs font-medium text-white shadow-lg dark:bg-gray-100 dark:text-gray-900 ${block ? 'whitespace-normal leading-relaxed' : 'whitespace-nowrap'}`}>
            {text}
          </span>
        </span>
      )}
    </Container>
  );
}
